/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// PDP js code starts here

// Tab click
document.addEventListener('click', function (e) {
  const tabLink = e.target.closest('.tab-link');
  if (!tabLink) return;

  const tabID = tabLink.getAttribute('data-tab');

  // Remove active from sibling tab links
  tabLink.parentElement.querySelectorAll('.tab-link').forEach(el => {
    el.classList.remove('active');
  });

  tabLink.classList.add('active');

  // Activate corresponding tab content
  const sizechart = tabLink.closest('.sizechart-inn');
  if (sizechart) {
    const activeTab = sizechart.querySelector('#tab-' + tabID);
    if (activeTab) {
      activeTab.classList.add('active');
      Array.from(activeTab.parentElement.children).forEach(sibling => {
        if (sibling !== activeTab) sibling.classList.remove('active');
      });
    }
  }

  document.querySelectorAll('.dimension-selected').forEach(el => {
    el.classList.remove('active-frame');
  });

  document.querySelectorAll('.dimension_details').forEach(el => {
    el.classList.remove('active');
  });

  document.querySelectorAll('.flap_dimension').forEach(el => {
    el.classList.add('active');
  });

  document.querySelectorAll('.flap-selected').forEach(el => {
    el.classList.add('active-frame');
  });
});

// Click + mouseover handlers
['click', 'mouseover'].forEach(eventType => {

  document.addEventListener(eventType, function (e) {

    if (e.target.closest('.dimension_details')) {
      document.querySelectorAll('.dimension_details').forEach(el => {
        el.classList.remove('active');
      });
      e.target.closest('.dimension_details').classList.add('active');
    }

    if (e.target.closest('.flap_dimension')) {
      document.querySelectorAll('.dimension-selected').forEach(el => {
        el.classList.remove('active-frame');
      });
      document.querySelectorAll('.flap-selected').forEach(el => {
        el.classList.add('active-frame');
      });
    }

    if (e.target.closest('.outsideframe_dimension')) {
      document.querySelectorAll('.dimension-selected').forEach(el => {
        el.classList.remove('active-frame');
      });
      document.querySelectorAll('.frame-selected').forEach(el => {
        el.classList.add('active-frame');
      });
    }

    if (e.target.closest('.rough_cut_out')) {
      document.querySelectorAll('.dimension-selected').forEach(el => {
        el.classList.remove('active-frame');
      });
      document.querySelectorAll('.roughcut-selected').forEach(el => {
        el.classList.add('active-frame');
      });
    }

  });

});


document.body.addEventListener('mouseover', handleCellHover);
document.body.addEventListener('mouseout', handleCellHover);

function handleCellHover(e) {
  const td = e.target.closest('.extra-size-chart .first_table table td');
  if (!td) return;

  // Ignore internal mouse movements
  if (
    e.type === 'mouseout' &&
    td.contains(e.relatedTarget)
  ) {
    return;
  }

  let cellindex = 0;

  if (e.type === 'mouseover') {

    td.classList.add('cell');

    const cells = Array.from(td.parentElement.children);
    for (let i = 0; i < cells.length; i++) {
      if (cells[i].classList.contains('cell')) {
        td.classList.remove('cell');
        cellindex = i;
        break;
      }
    }

    td.parentElement.classList.add('row');

    const rows = Array.from(td.parentElement.parentElement.children);
    for (let i = 0; i < rows.length; i++) {
      const tds = rows[i].children;

      if (tds[cellindex]) {
        tds[cellindex].classList.add('hover');
      }

      if (rows[i].classList.contains('row')) {
        for (let j = 0; j <= cellindex; j++) {
          if (tds[j]) tds[j].classList.add('hover');
        }
        td.parentElement.classList.remove('row');
        break;
      }
    }

    // THEAD hover
    const table = td.closest('table');
    if (table) {
      table.querySelectorAll('thead tr').forEach(row => {
        row.classList.add('headingg');
        const ths = row.children;
        if (ths[cellindex]) ths[cellindex].classList.add('hover');
      });
    }

  } else {
    // Proper cleanup
    document
      .querySelectorAll('.extra-size-chart td.hover, .extra-size-chart th.hover')
      .forEach(el => el.classList.remove('hover'));
  }
}

// Make first thumbnail active in each group
document.addEventListener('DOMContentLoaded', function () {
  document
    .querySelectorAll('.installation-topdetails .steps-wrapper .steps-block .img-thumbnails')
    .forEach(wrapper => {
      const firstImg = wrapper.querySelector('.img-container');
      if (firstImg) firstImg.classList.add('active');
    });
});


// Hover on thumbnail image
document.addEventListener('mouseover', function (e) {
  const img = e.target.closest('.installation-topdetails .steps-wrapper .steps-block .img-container img');
  if (!img) return;

  const src = img.getAttribute('src');
  const stepsBlock = img.closest('.steps-info-block');

  // Update main image
  if (stepsBlock) {
    const mainImg = stepsBlock.querySelector('.main-img-container img');
    if (mainImg) mainImg.src = src;
  }

  const container = img.parentElement;

  // Remove active from siblings
  container.parentElement
    .querySelectorAll('.img-container.active')
    .forEach(el => el.classList.remove('active'));

  // Add active to current
  container.classList.add('active');
});

let nav_product = document.querySelector('.sticky-nav-tabs-container');
let navTop = 0;

function sticky_relocate() {
  if (!nav_product) return;

  var windowTop = window.pageYOffset || document.documentElement.scrollTop;

  if (windowTop > navTop) {
    document.body.classList.add('stick-nav-tabs');
  } else {
    document.body.classList.remove('stick-nav-tabs');
  }
}

document.addEventListener('DOMContentLoaded', function () {
  nav_product = document.querySelector('.sticky-nav-tabs-container');
  if (!nav_product) return;

  // calculate once
  navTop = nav_product.offsetTop;

  window.addEventListener('scroll', sticky_relocate);
  sticky_relocate();
});

document.addEventListener('DOMContentLoaded', () => {
  const nav_active = document.querySelector('.nav-tab');
  if (!nav_active) return;

  nav_active.addEventListener('click', (e) => {
    const tab = e.target.closest('.sticky-nav-tab');
    if (!tab) return;

    // remove active from all
    nav_active.querySelectorAll('.sticky-nav-tab').forEach(el => {
      el.classList.remove('active');
    });

    // add active to clicked
    tab.classList.add('active');
  });
  document.querySelectorAll('.template-product a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();

      const id = this.getAttribute('href').substring(1);
      const target = document.getElementById(id);
      const headerOffset = 230;

      if (!target) return;

      const offsetPosition =
        target.getBoundingClientRect().top + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    });
  });
  document.querySelectorAll('.template-product  .accordion-pdp').forEach(accordion => {
    accordion.addEventListener('click', function () {
      this.classList.toggle('active');
      this.nextElementSibling.classList.toggle('active');
    });
  });
});

// Scroll down to main reviews widget on click star ratings
document.addEventListener('DOMContentLoaded', function() {
  const badge = document.querySelector('.stamped-product-reviews-badge');
  const reviewSection = document.querySelector('#stamped-main-widget');

  if (badge && reviewSection) {
    badge.addEventListener('click', function(e) {
      e.preventDefault();
      reviewSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }
});



// PDP js code ends here


// PDP js code ends here

document.addEventListener("DOMContentLoaded", function () {

  const backToTop = document.querySelector(".back-to-top");

  window.addEventListener("scroll", function () {
    if (window.scrollY > 300) {
      backToTop.style.opacity = "1";
      backToTop.style.pointerEvents = "auto";
    } else {
      backToTop.style.opacity = "0";
      backToTop.style.pointerEvents = "none";
    }
  });

  backToTop.addEventListener("click", function(e) {
    e.preventDefault();

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  });

});

const interval = setInterval(() => { 
  const host = document.querySelector('.js-instalments-form shopify-payment-terms'); 
  if (host && host.shadowRoot) { 
    clearInterval(interval); 
    // Avoid injecting duplicate styles 
    if (!host.shadowRoot.querySelector('#payment-custom-style')) { 
    const style = document.createElement('style'); 
      style.id = 'payment-custom-style'; 
      style.innerHTML = ` 
           #shopify-installments{
            display: inline-block;
            position: relative;
           }
          #shopify-installments-content + span{
            position: absolute;
            width: 64px;
            height: 27px;
            right: 0;
            top: 0;
            opacity: 0;
            cursor: pointer;
          }
          #shopify-installments-content + span button{
                 height: 27px;
          } 
      `; 
      host.shadowRoot.appendChild(style); 
    } 
  } 
}, 200); 